import { Alert, Box, Chip, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "../global/CustomButton";
import { Loading } from "../global/Loading";

const codeSx = {
  fontFamily: "monospace",
  fontSize: 12,
  overflowWrap: "anywhere",
};

const chipSx = (color) => ({
  height: 22,
  fontSize: 12,
  ...(color === "green" && {
    backgroundColor: "rgba(34, 197, 94, 0.16)",
    color: "rgb(17, 141, 87)",
  }),
  ...(color === "yellow" && {
    backgroundColor: "rgba(255, 171, 0, 0.16)",
    color: "rgb(183, 110, 0)",
  }),
});

const SourceCard = ({ source, onUseAsApi }) => {
  const [record] = source.records;
  const sampleFields = (record?.fields ?? [])
    .filter((field) => field.likelyStatus)
    .slice(0, 4);

  return (
    <Box sx={{ border: "1px solid #eef0f2", borderRadius: "8px", p: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 0.75,
        }}
      >
        <Chip size="small" label={source.method} sx={chipSx()} />
        {source.worksAsApiCheck ? (
          <Chip
            size="small"
            label="works as API check"
            sx={chipSx("green")}
          />
        ) : (
          <Tooltip title={source.apiCheckError ?? ""}>
            <Chip
              size="small"
              label="only works in the browser"
              sx={chipSx("yellow")}
            />
          </Tooltip>
        )}
        <Typography variant="body2" color="text.secondary">
          {source.recordCount} record{source.recordCount > 1 ? "s" : ""} about
          ADA
        </Typography>
      </Box>
      <Typography sx={{ ...codeSx, mb: 0.75 }}>{source.url}</Typography>
      {record && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {record.matchedBy}
          {sampleFields.length > 0 && (
            <>
              {" · "}
              <Box component="span" sx={codeSx}>
                {sampleFields
                  .map((field) => `${field.key}: ${JSON.stringify(field.value)}`)
                  .join(", ")}
              </Box>
            </>
          )}
        </Typography>
      )}
      {!source.worksAsApiCheck && source.apiCheckError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Without the browser: {source.apiCheckError}
        </Typography>
      )}
      {source.worksAsApiCheck && (
        <Box sx={{ display: "flex" }}>
          <CustomButton
            buttonText="Use as API check"
            color="black"
            onClick={() => onUseAsApi(source.url)}
          />
        </Box>
      )}
    </Box>
  );
};

// Loads a scrape check's page the way the check does and lists the JSON
// the page fetched that mentions ADA. Switching to such a source makes the
// check faster and independent of the page layout.
const JsonSourceFinder = ({ script, onUseAsApi }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState();

  const search = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/exchanges/findJsonSources/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt,
          },
          body: JSON.stringify({ statusScript: script }),
        }
      );
      const data = await response.json().catch(() => ({}));
      setResult(
        response.ok
          ? data
          : {
              errorMessage:
                data.message || `Request failed (${response.status})`,
              stepErrors: [],
              sources: [],
            }
      );
    } catch (e) {
      setResult({ errorMessage: e.message, stepErrors: [], sources: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #dfe3e8", borderRadius: "12px", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            Find a JSON API behind this page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loads the page like the check does and lists the data it fetches
            about ADA. An API check is faster and does not break when the page
            layout changes.
          </Typography>
        </Box>
        <CustomButton
          buttonText={result ? "Search again" : "Search page"}
          color="blackOutlined"
          disabled={!script?.url || loading}
          onClick={search}
        />
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Loading />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading the page and running its steps, this can take up to a
            minute
          </Typography>
        </Box>
      )}

      {!loading && result && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {result.errorMessage && (
            <Alert severity="error">{result.errorMessage}</Alert>
          )}
          {result.stepErrors?.length > 0 && (
            <Alert severity="info">
              Some steps of the check failed while loading the page, so data
              they would load may be missing:
              {result.stepErrors.map((error) => (
                <Box key={error} sx={{ ...codeSx, mt: 0.5 }}>
                  {error}
                </Box>
              ))}
            </Alert>
          )}
          {!result.errorMessage && !result.sources.length && (
            <Alert severity="warning">
              None of the {result.jsonResponses} JSON responses this page
              loaded mention ADA. The status is probably in the page itself,
              so keep the scrape check.
            </Alert>
          )}
          {result.sources.map((source) => (
            <SourceCard
              key={source.url}
              source={source}
              onUseAsApi={onUseAsApi}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default JsonSourceFinder;
